import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Upload, message, Modal, Spin } from 'antd';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { getColeccionItemImageUrl, deleteColeccionItemImage } from 'api/coleccionItemImage';
import { useColeccionItemImageUpload } from 'hooks/useColeccionItemImageUpload';

// Componente reusable para subir/mostrar la foto de un item del catalogo.
//
// Modos de uso:
//   1) itemId conocido (edicion): el upload ocurre inmediatamente al
//      seleccionar el archivo (presign -> PUT -> confirm) y se llama onChange
//      con la nueva key.
//   2) itemId ausente (creacion): el archivo queda pendiente en estado local.
//      El parent llama imperativamente ref.current.uploadPending(newItemId)
//      despues de crear el item — asi se sube y confirma en un segundo paso.
//
// Props:
//   value: S3 key actual o null
//   onChange(keyOrNull): callback
//   itemId: number | null | undefined
//   disabled: boolean

const ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const validate = (file) => {
  if (!ALLOWED_MIMES.includes(file.type)) {
    message.error('Formato no soportado. Usá JPG, PNG o WebP.');
    return false;
  }
  if (file.size > MAX_SIZE_BYTES) {
    message.error(`El archivo supera los ${MAX_SIZE_MB} MB.`);
    return false;
  }
  return true;
};

const ColeccionItemImageUpload = forwardRef(function ColeccionItemImageUpload(
  { value, onChange, itemId, disabled },
  ref
) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const { upload, loading: uploading } = useColeccionItemImageUpload();

  // Resolver preview cuando hay value (key persistida) + itemId.
  useEffect(() => {
    let canceled = false;
    if (!value || !itemId) {
      if (!pendingFile) setPreviewUrl(null);
      return undefined;
    }
    setPreviewLoading(true);
    getColeccionItemImageUrl(itemId)
      .then((url) => {
        if (!canceled) setPreviewUrl(url);
      })
      .catch(() => {
        if (!canceled) setPreviewUrl(null);
      })
      .finally(() => {
        if (!canceled) setPreviewLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [value, itemId, pendingFile]);

  // Preview local cuando hay pendingFile (sin itemId todavia).
  useEffect(() => {
    if (!pendingFile) return undefined;
    const url = URL.createObjectURL(pendingFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const beforeUpload = useCallback(
    async (file) => {
      if (!validate(file)) return Upload.LIST_IGNORE;
      if (itemId) {
        try {
          const key = await upload(itemId, file);
          onChange?.(key);
          message.success('Foto actualizada');
        } catch (err) {
          message.error(err?.message || 'No se pudo subir la foto');
        }
      } else {
        setPendingFile(file);
        onChange?.('__pending__');
      }
      return Upload.LIST_IGNORE;
    },
    [itemId, upload, onChange]
  );

  const handleRemove = useCallback(async () => {
    if (pendingFile) {
      setPendingFile(null);
      onChange?.(null);
      return;
    }
    if (itemId && value) {
      try {
        await deleteColeccionItemImage(itemId);
        onChange?.(null);
        setPreviewUrl(null);
        message.success('Foto eliminada');
      } catch (err) {
        message.error(err?.message || 'No se pudo eliminar la foto');
      }
    }
  }, [pendingFile, itemId, value, onChange]);

  useImperativeHandle(
    ref,
    () => ({
      // Llamado por el parent tras crear el item para subir el archivo pendiente.
      // Si no hay pending, resuelve sin hacer nada.
      async uploadPending(newItemId) {
        if (!pendingFile) return null;
        if (!newItemId) throw new Error('uploadPending requiere itemId');
        const key = await upload(newItemId, pendingFile);
        setPendingFile(null);
        onChange?.(key);
        return key;
      },
      hasPending() {
        return !!pendingFile;
      },
      clearPending() {
        setPendingFile(null);
      },
    }),
    [pendingFile, upload, onChange]
  );

  const showingSomething = !!previewUrl;
  const isBusy = uploading || previewLoading;

  return (
    <>
      <Upload
        listType="picture-card"
        showUploadList={false}
        beforeUpload={beforeUpload}
        disabled={disabled || uploading}
        accept={ALLOWED_MIMES.join(',')}
      >
        {showingSomething ? (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <img
              src={previewUrl}
              alt="Foto del item"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onClick={(e) => {
                e.stopPropagation();
                setPreviewOpen(true);
              }}
            />
            {isBusy ? (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255,255,255,0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Spin />
              </div>
            ) : null}
          </div>
        ) : (
          <div>
            {isBusy ? <LoadingOutlined /> : <PlusOutlined />}
            <div style={{ marginTop: 8 }}>{uploading ? 'Subiendo…' : 'Agregar foto'}</div>
          </div>
        )}
      </Upload>
      {showingSomething && !disabled ? (
        <a
          onClick={(e) => {
            e.preventDefault();
            handleRemove();
          }}
          style={{ fontSize: 12 }}
        >
          Quitar foto
        </a>
      ) : null}

      <Modal open={previewOpen} footer={null} onCancel={() => setPreviewOpen(false)} centered>
        {previewUrl ? <img alt="Foto del item" style={{ width: '100%' }} src={previewUrl} /> : null}
      </Modal>
    </>
  );
});

export default ColeccionItemImageUpload;
